/**
 * Unified Storage Service (Facade for SQL/localStorage)
 * @deprecated Use survey-storage.service directly for new code
 *
 * Maintains backward-compatible async API while delegating to survey-storage.service
 * which uses StorageService with SQL/localStorage adapters.
 */

import * as SurveyStorage from './survey-storage.service'
import { reportError } from '@/lib/error-reporting'

/** Export file format version */
const FILE_VERSION = '1.0'

/** Application name for file validation */
const APPLICATION_NAME = 'sitecompass-portal'

import type {
  UnifiedStorageExportData,
  SurveyData,
  InitializeSurveyOptions,
} from '@/types/unified-storage.types'
import type { SurveyAssumptions } from '@/types/assumptions.types'
import type { SurveyPortMapping } from '@/types/portMapping.types'
import type { SurveyMasterEquipmentSettings } from '@/types/master-equipment.types'

/**
 * Initialize or get survey data
 */
export async function initializeSurvey(options: InitializeSurveyOptions): Promise<SurveyData> {
  return await SurveyStorage.initializeSurvey(options)
}

/**
 * Get survey data by ID
 */
export async function getSurveyData(surveyId: string): Promise<SurveyData | null> {
  return await SurveyStorage.getSurveyData(surveyId)
}

/**
 * Get active survey ID
 */
export function getActiveSurveyId(): string | null {
  return SurveyStorage.getActiveSurveyId()
}

/**
 * Update survey assumptions
 */
export async function updateSurveyAssumptions(
  surveyId: string,
  assumptions: SurveyAssumptions
): Promise<SurveyAssumptions> {
  return await SurveyStorage.updateSurveyAssumptions(surveyId, assumptions)
}

/**
 * Update survey port mapping
 */
export async function updateSurveyPortMapping(
  surveyId: string,
  portMapping: SurveyPortMapping
): Promise<void> {
  await SurveyStorage.updateSurveyPortMapping(surveyId, portMapping)
}

/**
 * Update survey master equipment settings
 */
export async function updateSurveyMasterEquipment(
  surveyId: string,
  masterEquipment: SurveyMasterEquipmentSettings
): Promise<void> {
  await SurveyStorage.updateSurveyMasterEquipment(surveyId, masterEquipment)
}

/**
 * Generic update survey data with partial updates
 */
export async function updateSurveyData(
  surveyId: string,
  updates: Partial<SurveyData>
): Promise<void> {
  const surveyData = await SurveyStorage.getSurveyData(surveyId)
  if (surveyData) {
    Object.assign(surveyData, updates)
    surveyData.updatedAt = new Date().toISOString()

    // Save updated data
    if (updates.assumptions) {
      await SurveyStorage.updateSurveyAssumptions(surveyId, surveyData.assumptions)
    }
    if (updates.portMapping) {
      await SurveyStorage.updateSurveyPortMapping(surveyId, surveyData.portMapping)
    }
    if (updates.masterEquipment) {
      await SurveyStorage.updateSurveyMasterEquipment(surveyId, surveyData.masterEquipment)
    }
  }
}

/**
 * Export complete storage to file
 */
export async function exportToFile(): Promise<UnifiedStorageExportData> {
  const surveys = await SurveyStorage.getAllSurveys()
  return {
    fileVersion: FILE_VERSION,
    exportedAt: new Date().toISOString(),
    application: APPLICATION_NAME,
    data: {
      surveys,
      activeSurveyId: SurveyStorage.getActiveSurveyId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    },
  }
}

/**
 * Import storage from file
 * Merges with existing data
 */
export async function importFromFile(exportData: UnifiedStorageExportData): Promise<boolean> {
  try {
    // Validate file version
    if (exportData.fileVersion !== FILE_VERSION) {
      console.warn(`Unsupported file version: ${exportData.fileVersion}`)
      return false
    }

    // Validate application
    if (exportData.application !== APPLICATION_NAME) {
      console.warn(`Invalid application: ${exportData.application}`)
      return false
    }

    // Import each survey
    for (const [surveyId, surveyData] of Object.entries(exportData.data.surveys)) {
      // Convert date strings back to Date objects for port mapping templates
      if (surveyData.portMapping?.templates) {
        surveyData.portMapping.templates.forEach(template => {
          template.createdAt = new Date(template.createdAt)
          template.updatedAt = new Date(template.updatedAt)
        })
      }

      await SurveyStorage.initializeSurvey({ surveyId, surveyName: surveyData.surveyName })
      await SurveyStorage.updateSurveyAssumptions(surveyId, surveyData.assumptions)
      await SurveyStorage.updateSurveyPortMapping(surveyId, surveyData.portMapping)
      await SurveyStorage.updateSurveyMasterEquipment(surveyId, surveyData.masterEquipment)
    }

    return true
  } catch (error) {
    reportError(error, { where: 'unified-storage.service.importFromFile' })
    return false
  }
}

/**
 * Download complete storage as JSON file
 */
export async function downloadBackupFile(): Promise<void> {
  const data = await exportToFile()
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `sitecompass-backup-${Date.now()}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Clear all storage data
 */
export async function clearAllData(): Promise<void> {
  await SurveyStorage.clearAllData()
}

/**
 * Delete a specific survey
 */
export async function deleteSurvey(surveyId: string): Promise<void> {
  await SurveyStorage.deleteSurvey(surveyId)
}

/**
 * Get all surveys
 */
export async function getAllSurveys(): Promise<Record<string, SurveyData>> {
  return await SurveyStorage.getAllSurveys()
}
